import React, { memo } from "react";
import { formatCurrency, useTranslate } from "helpers";
import { ReactSVG } from "react-svg";
import { Button } from "ui/components/Button/Button";
import { MarketplaceGridCardProps } from "./types";

// Grid kartica marketplacea — isti raspored kao VehicleGridCard, ali bez internih
// podataka: prikazuje se samo prodajna cijena, bez nabavne i bez tooltipa s maržama.
const EMPTY_VALUE = "—";

const MarketplaceGridCard: React.FC<MarketplaceGridCardProps> = ({ vehicle, onRowClick }) => {
  const { t } = useTranslate();

  const handleClick = () => {
    if (onRowClick) onRowClick(vehicle.id);
  };

  const handleDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRowClick) onRowClick(vehicle.id);
  };

  const power = vehicle.vehicleType?.maximumPowerKw != null
    ? `${vehicle.vehicleType.maximumPowerKw} kW${vehicle.vehicleType.maximumPowerHp != null ? ` (${vehicle.vehicleType.maximumPowerHp} HP)` : ""}`
    : null;

  // Uvijek isti redci, istim redoslijedom — vrijednost koje nema ide kao "—",
  // da kartice u istom redu ostanu jednake visine.
  const specs: { icon: string; value: string | null }[] = [
    { icon: "transmission", value: vehicle.vehicleType?.transmission?.name ?? null },
    { icon: "fuel", value: vehicle.vehicleType?.fuelType?.name ?? null },
    {
      icon: "speedometer",
      value: vehicle.kmTraveled != null ? `${vehicle.kmTraveled.toLocaleString("hr-HR")} km` : null,
    },
    { icon: "engine-capacity", value: power },
    {
      icon: "seat",
      value: vehicle.vehicleType?.seats ? `${vehicle.vehicleType.seats} sjedala` : null,
    },
    {
      icon: "door",
      value: vehicle.vehicleType?.numberOfDoors ? `${vehicle.vehicleType.numberOfDoors} vrata` : null,
    },
  ];

  const year = vehicle.firstRegistrationDate
    ? new Date(vehicle.firstRegistrationDate).getFullYear()
    : vehicle.vehicleModel?.year;

  return (
    <div
      className="relative h-full flex flex-col bg-white rounded-sm border border-gray-200 overflow-hidden cursor-pointer"
      onClick={handleClick}
    >
      <div className="relative aspect-[4/3] bg-gray-100">
        {vehicle.picture ? (
          <img
            src={vehicle.picture}
            alt={vehicle.name || "Vehicle"}
            className="w-full h-full object-cover object-bottom rounded-sm"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <span className="text-4xl text-gray-400 font-semibold">
              {vehicle.vehicleManufacturer?.name?.toString().charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-semibold text-gray-900 mb-2 truncate">
          {vehicle.vehicleManufacturer?.name} – {vehicle.vehicleModel?.name}
          {year ? ` (${year})` : ""}
        </h3>

        <div className="mb-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {specs.map((spec) => (
              <div key={spec.icon} className="flex items-center gap-2 text-sm text-gray-400">
                <ReactSVG src={`/media/icons/${spec.icon}.svg`} className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{spec.value ?? EMPTY_VALUE}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm text-gray-400 col-span-2">
              <ReactSVG src="/media/icons/location.svg" className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{vehicle.location?.locationName ?? EMPTY_VALUE}</span>
            </div>
          </div>
        </div>

        {/* Samo prodajna cijena — `mt-auto` drži cijenu i gumb na dnu kartice. */}
        <div className="mt-auto pt-3 border-t border-gray-100 mb-3 text-right">
          <div className="text-xs text-green-600 uppercase font-semibold">
            {t("admin.fleet.vehicle.detail.info.sellingsrice")}
          </div>
          <div className="text-md font-bold text-green-600 truncate">
            {formatCurrency.format(vehicle.price?.sellingPrice ?? 0)}
          </div>
        </div>

        <Button type="button" variant="primary" size="full" onClick={handleDetailsClick}>
          {t("admin.fleet.vehicle.grid.viewdetails")}
        </Button>
      </div>
    </div>
  );
};

export default memo(MarketplaceGridCard);
